import { formatNumber, formatPercent, formatPrice } from '@/utils/price';

import {
  Currency,
  ExchangeService,
  getCurrencyLabel,
  getExchangeServiceLabel,
  SpreadType,
} from './exchange';
import { User, UserRole } from './user';

export enum ActivityLogAction {
  CreatedUser = 'Created User',
  UpdatedUser = 'Updated User',
  DeletedUser = 'Deleted User',
  SavedWalletAddressOnOrder = 'Saved Wallet Address Placing Order',
  UpdatedUserWalletAddress = 'Update User Wallet Address',
  UpdatedUserLimits = 'Updated User Limits',
  ApprovedUserDocuments = 'Approved User Documents',
  SaveWalletKey = 'Save Wallet Key',
  SaveExchangeSettings = 'Save Exchange Settings',
  ChangeDefaultExchange = 'Change Default Exchange',
  ChangeExchangeEnabled = 'Change Exchange Enabled',
  ChangeExchangeSpread = 'Change Exchange Spread',
  PlacedOrder = 'Placed Order',
  IssuedContract = 'Issued Contract',
  CanceledTransaction = 'Canceled Transaction',
  CreditedTransaction = 'Credited Transaction',
  ConfirmedDeposit = 'Confirmed Deposit',
  Login = 'Login',
  PasswordRecovery = 'Password Recovery',
  PasswordChange = 'Password Change',
}

export type ActivityLog = {
  id: string;
  userId: string;
  action: ActivityLogAction;
  context: Record<string, unknown>;
  timestamp: Date;
};

export type ActivityLogLabelContext = {
  users: Map<string, User>;
};

export type FormatActivityLogLabelFn = (
  log: ActivityLog,
  context: ActivityLogLabelContext,
) => string;

export type ActivityLogChange = {
  name: string;
  oldValue?: string;
  newValue?: string;
};

const ACTIVITY_LOG_LABELS: Record<ActivityLogAction, string> = {
  [ActivityLogAction.CreatedUser]: 'Criou um usuário',
  [ActivityLogAction.UpdatedUser]: 'Atualizou um usuário',
  [ActivityLogAction.DeletedUser]: 'Deletou um usuário',
  [ActivityLogAction.SavedWalletAddressOnOrder]:
    'Salvou o endereço da carteira ao realizar uma compra',
  [ActivityLogAction.UpdatedUserWalletAddress]: 'Atualizou o endereço da carteira do usuário',
  [ActivityLogAction.UpdatedUserLimits]: 'Atualizou os limites do usuário',
  [ActivityLogAction.ApprovedUserDocuments]: 'Aprovou os documentos do cliente',
  [ActivityLogAction.SaveWalletKey]: 'Alterou a chave da carteira',
  [ActivityLogAction.SaveExchangeSettings]: 'Alterou as configurações de exchange',
  [ActivityLogAction.ChangeDefaultExchange]: 'Alterou a exchange padrão',
  [ActivityLogAction.ChangeExchangeEnabled]: 'Ativou/desativou uma exchange',
  [ActivityLogAction.ChangeExchangeSpread]: 'Alterou o spread de uma exchange',
  [ActivityLogAction.IssuedContract]: 'Emitiu um contrato',
  [ActivityLogAction.PlacedOrder]: 'Realizou uma compra',
  [ActivityLogAction.CanceledTransaction]: 'Cancelou uma transação',
  [ActivityLogAction.CreditedTransaction]: 'Creditou uma transação',
  [ActivityLogAction.ConfirmedDeposit]: 'Confirmou um depósito',
  [ActivityLogAction.Login]: 'Login',
  [ActivityLogAction.PasswordRecovery]: 'Solicitou recuperação de senha',
  [ActivityLogAction.PasswordChange]: 'Alterou a senha',
};

export const ACTIVITY_LOG_ACTION_OPTIONS = Object.entries(ACTIVITY_LOG_LABELS).map(
  ([key, label]) => ({
    value: key as ActivityLogAction,
    label,
  }),
);

export function getActivityLogLabel(action: ActivityLogAction): string {
  return ACTIVITY_LOG_LABELS[action] || 'Ação desconhecida';
}

const ACTIVITY_LOG_DESCRIPTIONS: Record<ActivityLogAction, FormatActivityLogLabelFn> = {
  [ActivityLogAction.CreatedUser]: (log, context) => {
    return `Criou o ${formatUser(log.context.createdUserId, context)}`;
  },
  [ActivityLogAction.UpdatedUser]: (log, context) => {
    const changes = (log.context.changes || []) as ActivityLogChange[];

    if (changes.length > 0) {
      const changesDescriptions = changes.map((change) => {
        return `${change.name} de ${
          typeof change.oldValue === 'undefined' ? 'nulo' : change.oldValue
        } para ${change.newValue}`;
      });
      const user = formatUser(log.context.updatedUserId, context);
      return `Atualizou os dados do ${user}: ${changesDescriptions.join('\n')}`;
    }
    return `Alterou os dados do ${formatUser(log.context.updatedUserId, context)}`;
  },
  [ActivityLogAction.DeletedUser]: (log, context) => {
    return `Deletou o ${formatUser(log.context.deletedUserId, context)}`;
  },
  [ActivityLogAction.SavedWalletAddressOnOrder]: (log, context) => {
    const ownKey = log.userId === log.context.userId;
    const walletAddress = log.context.walletAddress as string;
    return ownKey
      ? `Salvou seu endereço de carteira "${walletAddress}" ao realizar uma compra`
      : `Salvou o endereço da carteira "${walletAddress}" do ${formatUser(log.context.userId, context)} ao realizar uma compra`;
  },
  [ActivityLogAction.UpdatedUserWalletAddress]: (log, context) => {
    const userId = log.context.userId;
    const newValue = log.context.newValue as string;
    const oldValue = log.context.oldValue as string | undefined;
    return oldValue
      ? `Atualizou o endereço da carteira do ${formatUser(userId, context)} de "${oldValue}" para "${newValue}"`
      : `Atualizou o endereço da carteira do ${formatUser(userId, context)} para "${newValue}"`;
  },
  [ActivityLogAction.UpdatedUserLimits]: (log, context) => {
    const userId = log.context.userId;
    const limitTypes: Record<string, string> = {
      transaction: 'por transação',
      daily: 'diário',
      monthly: 'mensal',
    };
    const limitType = limitTypes[log.context.limitType as string] || '';
    const newLimit =
      typeof log.context.newValue === 'number'
        ? formatPrice(log.context.newValue as number)
        : undefined;
    const oldLimit =
      typeof log.context.oldValue === 'number'
        ? formatPrice(log.context.oldValue as number)
        : undefined;
    if (!newLimit) {
      return `Removeu o limite ${limitType} do ${formatUser(userId, context)}.${oldLimit ? ` Anteriormente era ${oldLimit}` : ''}`;
    }
    return oldLimit
      ? `Atualizou o limite ${limitType} do ${formatUser(userId, context)} de ${oldLimit} para ${newLimit}`
      : `Atualizou o limite ${limitType} do ${formatUser(userId, context)} para ${newLimit}`;
  },
  [ActivityLogAction.ApprovedUserDocuments]: (log, context) => {
    return `Aprovou os documentos do ${formatUser(log.context.userId, context)}`;
  },
  [ActivityLogAction.SaveWalletKey]: () => 'Alterou as configurações da chave da carteira',
  [ActivityLogAction.SaveExchangeSettings]: () => 'Alterou as configurações de exchange',
  [ActivityLogAction.ChangeDefaultExchange]: (log) => {
    const previousExchange = log.context.previousDefaultExchange as ExchangeService | undefined;
    const newExchange = log.context.newDefaultExchange as ExchangeService | undefined;
    if (!previousExchange || !newExchange) return 'Alterou a exchange padrão';
    return `Alterou a exchange padrão de ${getExchangeServiceLabel(previousExchange)} para ${getExchangeServiceLabel(newExchange)}`;
  },
  [ActivityLogAction.ChangeExchangeEnabled]: (log) => {
    const exchange = log.context.exchange as ExchangeService | undefined;
    const enabled = log.context.enabled as boolean | undefined;
    if (!exchange || typeof enabled !== 'boolean') return 'Ativou/desativou uma exchange';
    return `${enabled ? 'Ativou' : 'Desativou'} a exchange ${getExchangeServiceLabel(exchange)}`;
  },
  [ActivityLogAction.ChangeExchangeSpread]: (log) => {
    const exchange = log.context.exchange as ExchangeService | undefined;
    const previousSpreadValue = log.context.previousSpread as number | undefined;
    const previousSpreadType = log.context.previousSpreadType as SpreadType | undefined;
    const newSpreadValue = log.context.spread as number | undefined;
    const newSpreadType = log.context.spreadType as SpreadType | undefined;
    if (
      !exchange ||
      typeof previousSpreadValue !== 'number' ||
      !previousSpreadType ||
      typeof newSpreadValue !== 'number' ||
      !newSpreadType
    ) {
      return 'Alterou o spread de uma exchange';
    }

    const previousSpread =
      previousSpreadType === SpreadType.Percentage
        ? formatPercent(previousSpreadValue, false)
        : formatPrice(previousSpreadValue);
    const newSpread =
      newSpreadType === SpreadType.Percentage
        ? formatPercent(newSpreadValue, false)
        : formatPrice(newSpreadValue);

    return `Alterou o spread da exchange ${getExchangeServiceLabel(exchange)} de ${previousSpread} para ${newSpread}`;
  },
  [ActivityLogAction.PlacedOrder]: (log, context) => {
    const currency = log.context.currency
      ? getCurrencyLabel(log.context.currency as Currency)
      : undefined;
    const amount = log.context.amount ? formatNumber(log.context.amount as number) : undefined;
    const totalValue = log.context.totalValue
      ? formatPrice(log.context.totalValue as number)
      : undefined;
    const clientId = log.context.clientId as string | undefined;

    if (clientId) {
      if (!currency || !amount || !totalValue)
        return `Realizou uma compra para o ${formatUser(clientId, context)}`;
      return `Realizou uma compra de ${amount} ${currency} no valor de ${totalValue} para o ${formatUser(clientId, context)}`;
    } else {
      if (!currency || !amount || !totalValue) return 'Realizou uma compra';
      return `Realizou uma compra de ${amount} ${currency} no valor de ${totalValue}`;
    }
  },
  [ActivityLogAction.IssuedContract]: (log, context) =>
    formatOrderActionLogDescription('Emitiu um contrato para', log, context),
  [ActivityLogAction.CanceledTransaction]: (log, context) =>
    formatOrderActionLogDescription('Cancelou', log, context),
  [ActivityLogAction.CreditedTransaction]: (log, context) =>
    formatOrderActionLogDescription('Creditou', log, context),
  [ActivityLogAction.ConfirmedDeposit]: (log, context) => {
    const amount = formatPrice(log.context.amount as number);
    return `Confirmou um depósito de ${amount} do ${formatUser(log.context.userId, context)}`;
  },
  [ActivityLogAction.Login]: () => 'Se autenticou no sistema',
  [ActivityLogAction.PasswordRecovery]: () => 'Solicitou recuperação de senha',
  [ActivityLogAction.PasswordChange]: () => 'Alterou a senha',
};

export function formatActivityLogDescription(
  log: ActivityLog,
  context: ActivityLogLabelContext,
): string {
  const formatFn = ACTIVITY_LOG_DESCRIPTIONS[log.action];
  return formatFn ? formatFn(log, context) : 'Ação desconhecida';
}

function formatOrderActionLogDescription(
  action: string,
  log: ActivityLog,
  context: ActivityLogLabelContext,
) {
  const currency = log.context.currency
    ? getCurrencyLabel(log.context.currency as Currency)
    : undefined;
  const amount = log.context.amount ? formatNumber(log.context.amount as number) : undefined;
  const totalValue = log.context.totalValue
    ? formatPrice(log.context.totalValue as number)
    : undefined;
  const userId = log.context.userId as string | undefined;

  const user = context.users.get(userId as string);
  const userType = user?.role === UserRole.User ? 'cliente' : 'usuário';
  const userName = user?.name || 'desconhecido';
  if (!currency || !amount || !totalValue) {
    return `${action} uma compra do ${userType} ${userName}`;
  }
  return `${action} uma compra de ${amount} ${currency} no valor de ${totalValue} do ${userType} ${userName}`;
}

function formatUser(userId: unknown, context: ActivityLogLabelContext): string {
  const user = typeof userId === 'string' ? context.users.get(userId) : undefined;
  const userType = user?.role === UserRole.User ? 'cliente' : 'usuário';
  return `${userType} ${user?.name || 'desconhecido'}`;
}
